import { X } from "lucide-react";
import { type CSSProperties, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppState } from "../state/AppState";
import { Button } from "./Button";
import { LoginModal } from "./LoginModal";
import { ToastStack } from "./ToastStack";

export function Layout() {
  const { cart, isAdmin, logout, theme, toggleTheme, user } = useAppState();
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginCancelAction, setLoginCancelAction] = useState<(() => void) | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const isAdminPath = location.pathname.startsWith("/admin");
  const isHomePath = location.pathname === "/";
  const isProductDetailPath = /^\/products\/[^/]+$/.test(location.pathname);
  const isAboutPath = location.pathname === "/about";
  const isCheckoutPath = location.pathname === "/checkout";
  const homeNavbarStyle = isHomePath
    ? ({
        "--navbar-bg": theme === "dark" ? "rgba(17, 12, 24, 0.92)" : "rgba(255, 253, 250, 0.76)",
        "--orbit-nav-bg": theme === "dark" ? "rgba(17, 12, 24, 0.92)" : "rgba(255, 253, 250, 0.76)",
        "--orbit-panel-border": theme === "dark" ? "rgba(165, 122, 191, 0.24)" : "rgba(85, 58, 134, 0.13)",
        "--orbit-ink": theme === "dark" ? "#f5eefc" : "#261945",
        "--text-primary": theme === "dark" ? "#f5eefc" : "#261945",
        "--text-secondary": theme === "dark" ? "rgba(245, 238, 252, 0.72)" : "rgba(38, 25, 69, 0.64)",
        "--border-color": theme === "dark" ? "rgba(165, 122, 191, 0.24)" : "rgba(85, 58, 134, 0.13)",
        "--navbar-shadow": theme === "dark" ? "0 14px 45px rgba(0, 0, 0, 0.36)" : "0 14px 45px rgba(38, 25, 69, 0.08)"
      } as CSSProperties)
    : undefined;

  useLayoutEffect(() => {
    const body = document.body;
    const root = document.documentElement;
    root.classList.toggle("home-active-root", isHomePath);
    body.classList.toggle("home-active", isHomePath);
    body.classList.toggle("admin-mode", isAdminPath);
    body.classList.toggle("product-detail-active", isProductDetailPath);
    body.classList.toggle("about-active", isAboutPath);
    body.classList.toggle("checkout-active", isCheckoutPath);
    return () => {
      root.classList.remove("home-active-root");
      body.classList.remove("home-active", "admin-mode", "product-detail-active", "about-active", "checkout-active");
    };
  }, [isAboutPath, isAdminPath, isCheckoutPath, isHomePath, isProductDetailPath]);

  useEffect(() => {
    const navbar = document.getElementById("main-navbar");
    if (isHomePath) {
      navbar?.classList.remove("scrolled");
      return;
    }

    function onScroll() {
      const navbar = document.getElementById("main-navbar");
      if (!navbar) return;
      navbar.classList.toggle("scrolled", window.scrollY > 12);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHomePath]);

  function handleAuthClick() {
    if (user) {
      logout();
      navigate("/");
      return;
    }
    setLoginOpen(true);
  }

  const openLogin = useCallback((options?: { onCancel?: () => void }) => {
    setLoginCancelAction(() => options?.onCancel || null);
    setLoginOpen(true);
  }, []);

  const outletContext = useMemo(() => ({ openLogin }), [openLogin]);

  const handleLoginClose = useCallback(() => {
    setLoginOpen(false);
    const onCancel = loginCancelAction;
    setLoginCancelAction(null);
    onCancel?.();
  }, [loginCancelAction]);

  const handleLoginAuthenticated = useCallback(() => {
    setLoginOpen(false);
    setLoginCancelAction(null);
  }, []);

  return (
    <div className={["app-shell", isAdminPath ? "admin-shell" : ""].filter(Boolean).join(" ")}>
      <a className="skip-link" href="#main-content">
        Skip To Content
      </a>
      <header id="main-navbar" className={menuOpen ? "navbar nav-open" : "navbar"} style={homeNavbarStyle}>
        <Link className="logo" to="/" translate="no" onClick={() => setMenuOpen(false)}>
          PROTO<span>DECCAN</span>
        </Link>

        <Link className="nav-cart-mobile" to="/cart" aria-label={`Cart With ${cart.length} Items`}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.00014 14H18.1359C19.1487 14 19.6551 14 20.0582 13.8112C20.4134 13.6448 20.7118 13.3777 20.9163 13.0432C21.1485 12.6633 21.2044 12.16 21.3163 11.1534L21.9013 5.88835C21.9355 5.58088 21.9525 5.42715 21.9031 5.30816C21.8597 5.20366 21.7821 5.11697 21.683 5.06228C21.5702 5 21.4155 5 21.1062 5H4.50014M2 2H3.24844C3.51306 2 3.64537 2 3.74889 2.05032C3.84002 2.09463 3.91554 2.16557 3.96544 2.25376C4.02212 2.35394 4.03037 2.48599 4.04688 2.7501L4.95312 17.2499C4.96963 17.514 4.97788 17.6461 5.03456 17.7462C5.08446 17.8344 5.15998 17.9054 5.25111 17.9497C5.35463 18 5.48694 18 5.75156 18H19M7.5 21.5H7.51M16.5 21.5H16.51M8 21.5C8 21.7761 7.77614 22 7.5 22C7.22386 22 7 21.7761 7 21.5C7 21.2239 7.22386 21 7.5 21C7.77614 21 8 21.2239 8 21.5ZM17 21.5C17 21.7761 16.7761 22 16.5 22C16.2239 22 16 21.7761 16 21.5C16 21.2239 16.2239 21 16.5 21C16.7761 21 17 21.2239 17 21.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Link>

        <button
          className="mobile-toggle"
          type="button"
          aria-label={menuOpen ? "Close Navigation" : "Open Navigation"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? <X size={22} aria-hidden="true" /> : <span aria-hidden="true">&#9776;</span>}
        </button>

        <nav className={menuOpen ? "nav-links open" : "nav-links"} aria-label="Primary Navigation">
          <NavLink to="/products" onClick={() => setMenuOpen(false)} className={({ isActive }) => (isActive ? "active" : undefined)}>
            Catalog
          </NavLink>
          <NavLink to="/cart" onClick={() => setMenuOpen(false)} className="nav-cart-dropdown">
            Cart
          </NavLink>
          <NavLink to="/cart" onClick={() => setMenuOpen(false)} className={({ isActive }) => `nav-cart-desktop${isActive ? " active" : ""}`.trim()} aria-label="Cart">
            Cart
            {cart.length ? <span className="nav-count">{cart.length}</span> : null}
          </NavLink>
          {user ? <NavLink to="/orders" onClick={() => setMenuOpen(false)} className={({ isActive }) => (isActive ? "active" : undefined)}>Orders</NavLink> : null}
          {user ? <NavLink to="/profile" onClick={() => setMenuOpen(false)} className={({ isActive }) => (isActive ? "active" : undefined)}>Profile</NavLink> : null}
          <button className="nav-auth" type="button" onClick={handleAuthClick} style={!user ? { color: "var(--accent-primary)" } : undefined}>
            {user ? "Logout" : "Login"}
          </button>
          <NavLink to="/about" onClick={() => setMenuOpen(false)} className={({ isActive }) => (isActive ? "active" : undefined)}>
            About Us
          </NavLink>
          {isAdmin ? <NavLink to="/admin" onClick={() => setMenuOpen(false)}>Admin</NavLink> : null}
          <button
            className="theme-toggle"
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "light" ? "Switch To Dark Theme" : "Switch To Light Theme"}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={theme === "light" ? undefined : { display: "none" }}>
              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={theme === "light" ? { display: "none" } : undefined}>
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          </button>
        </nav>
      </header>

      {isAdminPath ? <AdminSidebar /> : null}

      <main id="app-container">
        <div id="main-content" className="page">
          <Outlet context={outletContext} />
        </div>
      </main>

      {!isAboutPath ? (
        <footer className="site-footer">
          <div className="footer-content">
            <Link to="/about" className="footer-brand" translate="no" style={{ textDecoration: "none", cursor: "pointer" }}>
              PROTO<span>DECCAN</span>
            </Link>
            <nav className="footer-links" aria-label="Footer Navigation">
              <Link to="/about">About Us</Link>
              <Link to="/privacypolicy">Privacy Policy</Link>
              <Link to="/termsandconditions">Terms & Conditions</Link>
            </nav>
            <div className="footer-copy">© 2026 ProtoDeccan. All rights reserved.</div>
          </div>
        </footer>
      ) : null}

      <LoginModal open={loginOpen} onClose={handleLoginClose} onAuthenticated={handleLoginAuthenticated} />
      <ToastStack />
    </div>
  );
}

function AdminSidebar() {
  const items = [
    ["Overview", "/admin"],
    ["Orders", "/admin/orders"],
    ["Products", "/admin/products"],
    ["Settings", "/admin/settings"],
    ["Content", "/admin/content"],
    ["Legal", "/admin/legal"],
    ["Branding", "/admin/branding"],
    ["Theme", "/admin/theme"],
    ["FAQ Manager", "/admin/faq"],
    ["Logs", "/admin/logs"],
    ["Deep Clean", "/admin/deep-clean"]
  ];

  return (
    <aside className="admin-sidebar" aria-label="Admin Navigation">
      <div className="sidebar-title">Dashboard</div>
      {items.map(([label, to]) => (
        <NavLink key={to} to={to} end={to === "/admin"}>
          {label}
        </NavLink>
      ))}
      <Button variant="ghost" size="sm" onClick={() => window.location.assign("/")}>
        View Store
      </Button>
    </aside>
  );
}
